import { Injectable } from '@angular/core';
import { ApiRoutesV2, ApiRoutesV1 } from '../common/routes';
import { AjaxData, AjaxService } from './ajax.service';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, tap, filter, distinctUntilChanged, shareReplay, take } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { ReviewRequestType } from '../common/constants';
import { SmsUsagesService } from './sms-usages.service';
import { find, reject, isEmpty, isEqual } from 'lodash';

export interface Template {
  name: string;
  templateId: string;
  templateType: ReviewRequestType;
}

@Injectable({
  providedIn: 'root',
})
export class TemplateListingService {
  private templates$$: BehaviorSubject<Template[]> = new BehaviorSubject<Template[]>([]);
  private isLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  private defaultTemplateId$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public loadingPreview = false;

  constructor(private ajaxService: AjaxService, private smsUsagesService: SmsUsagesService) {}

  public get templates$(): Observable<Template[]> {
    return combineLatest([
      this.templates$$.asObservable().pipe(filter((templates) => templates.length > 0)),
      this.defaultTemplateId$,
    ]).pipe(
      map(([templates, defaultTemplateId]) => {
        const isTemplateDefault = (template: Template) => template.templateId === defaultTemplateId;
        const defaultTemplate = find(templates, isTemplateDefault);
        const restOfTemplates = reject(templates, isTemplateDefault);
        return defaultTemplate ? [defaultTemplate].concat(restOfTemplates) : restOfTemplates;
      }),
      distinctUntilChanged((t1, t2) => isEqual(t1, t2)),
      shareReplay(),
    );
  }

  public get isLoading$(): Observable<boolean> {
    return this.isLoading$$.asObservable();
  }

  public listTemplates(): void {
    combineLatest([this.ajaxService.get(ApiRoutesV2.templateList), this.smsUsagesService.hasSmsEnabled$]).subscribe(
      ([response, hasSmsEnabled]) => {
        this.isLoading$$.next(false);
        let templates: Template[] = response.body.data.email.map((data: any) => {
          return {
            name: data.name,
            templateId: data.template_id,
            templateType: ReviewRequestType.email,
          };
        });

        if (hasSmsEnabled) {
          templates = templates.concat(
            response.body.data.sms.map((data: any) => {
              return {
                name: data.name,
                templateId: data.template_id,
                templateType: ReviewRequestType.sms,
              };
            }),
          );
        }

        this.templates$$.next(templates);
      },
    );
  }

  public removeTemplateFromList(removedTemplateId: string): void {
    this.templates$$.next(
      this.templates$$.getValue().filter((template) => !(template.templateId === removedTemplateId)),
    );
  }

  public addTemplateToList(templateToAdd: Template, isNewDefault: boolean): void {
    if (isNewDefault) {
      this.templates$$.next([templateToAdd, ...this.templates$$.value]);
    } else {
      this.templates$$.next([...this.templates$$.value, templateToAdd]);
    }
  }

  public getPreview(templateId: string, templateType: ReviewRequestType): Observable<string> {
    this.loadingPreview = true;
    return this.ajaxService
      .get(ApiRoutesV2.templateRender, {
        params: {
          templateId: templateId,
          templateType: templateType,
        },
      })
      .pipe(
        map((response: HttpResponse<AjaxData>) => response.body.data),
        tap(() => (this.loadingPreview = false)),
      );
  }

  public get defaultTemplateId$(): Observable<string> {
    return this.defaultTemplateId$$.asObservable().pipe(
      filter((defaultId) => !isEmpty(defaultId)),
      distinctUntilChanged((id1, id2) => isEqual(id1, id2)),
      shareReplay(),
    );
  }

  public get hasOnlyOneEmailTemplate(): Observable<boolean> {
    return this.templates$.pipe(
      map((templates) => templates.filter((t) => t.templateType === ReviewRequestType.email).length === 1),
    );
  }

  public get hasOnlyOneSmsTemplate(): Observable<boolean> {
    return this.templates$.pipe(
      map((templates) => templates.filter((t) => t.templateType === ReviewRequestType.sms).length === 1),
    );
  }

  public fetchDefaultTemplateId(): void {
    this.ajaxService
      .get(ApiRoutesV1.defaultTemplateIdGet)
      .pipe(
        map((response: HttpResponse<AjaxData>) => response.body.data),
        map((fullTemplateId: string) => fullTemplateId.split(`/`)[6]),
      )
      .subscribe((defaultTemplateId) => this.defaultTemplateId$$.next(defaultTemplateId));
  }

  public getDefaultTemplateId(): Observable<string> {
    return this.ajaxService
      .get(ApiRoutesV1.defaultTemplateIdGet)
      .pipe(
        map((response: HttpResponse<AjaxData>) => response.body.data),
        map((fullTemplateId: string) => fullTemplateId.split(`/`)[6]),
      )
      .pipe(take(1));
  }

  public updateDefaultTemplateId(templateId: string): void {
    this.defaultTemplateId$$.next(templateId);
  }
}
